/**
 * Leer una consulta ENTERA, por páginas, y descartar respuestas viejas.
 *
 * De dónde sale (23-09-2026). PostgREST corta cada respuesta en 1.000 filas. El
 * Planificador pedía sus tareas de la más antigua a la más nueva en una sola
 * llamada, así que en una empresa con más de mil tareas lo que se quedaba fuera
 * era justo lo reciente: el calendario no lo pintaba y `?taskId=` (el «Ver
 * tarea» del asistente y de la voz) no encontraba la tarea. El bucle vivía
 * dentro de la página y solo lo vigilaba una expresión regular; aquí es una
 * función pura con su prueba de comportamiento (fetchAllPages.test.ts).
 *
 * Tres reglas:
 *  - Se piden páginas hasta que una viene CORTA. Una página vacía justo en el
 *    múltiplo de 1.000 también corta.
 *  - Una página que falla invalida la lista entera: medio calendario no se pinta
 *    como si fuera todo. Se devuelve el error y ninguna fila.
 *  - Paginar por posición (`range`) no es una foto fija: si alguien crea o
 *    cancela una fila entre página y página, una puede salir dos veces o
 *    ninguna. Lo primero se evita con `keyOf` (la segunda copia no entra); lo
 *    segundo no tiene arreglo sin una transacción, y lo corrige la siguiente
 *    lectura, que la página hace en cuanto cambia algo.
 */

export interface PageResult<T, E> {
  data: T[] | null;
  error: E | null;
}

export interface FetchAllPagesOptions<T> {
  /** Filas por página. 1.000 es el tope de PostgREST: pedir más no trae más. */
  pageSize?: number;
  /**
   * Tope de páginas. Si el servidor ignorara el `range` y devolviera siempre la
   * misma página llena, el bucle no acabaría nunca; con el tope se para y lo dice.
   */
  maxPages?: number;
  /** Identidad de una fila, para no meter dos veces la misma (ver arriba). */
  keyOf?: (row: T) => string;
}

export interface AllPages<T, E> {
  rows: T[];
  error: E | null;
  /** true si se paró por `maxPages` con la última página aún llena. */
  capped: boolean;
}

/**
 * `page(desde, hasta)` monta y lanza la consulta de esa página (índices
 * INCLUSIVOS, como `range` de postgrest-js). Se llama una vez por página y en
 * orden: la consulta tiene que llevar un orden TOTAL (con un desempate único al
 * final), o una fila puede salir en dos páginas o en ninguna.
 */
export async function fetchAllPages<T, E = unknown>(
  page: (from: number, to: number) => PromiseLike<PageResult<T, E>>,
  options: FetchAllPagesOptions<T> = {},
): Promise<AllPages<T, E>> {
  const pageSize = options.pageSize ?? 1000;
  const maxPages = options.maxPages ?? 100;
  const seen = options.keyOf ? new Set<string>() : null;
  const rows: T[] = [];
  for (let n = 0; n < maxPages; n++) {
    const from = n * pageSize;
    const { data, error } = await page(from, from + pageSize - 1);
    if (error) return { rows: [], error, capped: false };
    const lote = data ?? [];
    for (const row of lote) {
      if (seen) {
        const k = options.keyOf!(row);
        if (seen.has(k)) continue;
        seen.add(k);
      }
      rows.push(row);
    }
    if (lote.length < pageSize) return { rows, error: null, capped: false };
  }
  return { rows, error: null, capped: true };
}

/**
 * Turnos para descartar respuestas viejas. Cada lectura pide turno al empezar y,
 * antes de escribir en pantalla, pregunta si sigue siendo la última. Una
 * lectura lenta que termina después de otra más nueva ya no pisa su resultado.
 *
 *   const turnos = useRef(crearTurnos()).current;  // una vez por componente
 *   const sigoSiendoLaUltima = turnos();           // al empezar cada lectura
 *   …await…
 *   if (!sigoSiendoLaUltima()) return;             // antes de cada setState
 */
export function crearTurnos(): () => () => boolean {
  let ultimo = 0;
  return () => {
    const mio = ++ultimo;
    return () => mio === ultimo;
  };
}
